import { Box, Link, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Github, Linkedin, Mail, ArrowUpRight } from "lucide-react";
import { contentWidth, eyebrow, sectionHeading } from "features/landing/landingStyles";

const contactLinks = [
  {
    icon: Github,
    label: "GitHub",
    value: "github.com/SerPS92",
    href: "https://github.com/SerPS92",
  },
  {
    icon: Mail,
    label: "Email",
    value: "sergio.rianxo92@gmail.com",
    href: "mailto:sergio.rianxo92@gmail.com",
  },
  {
    icon: Linkedin,
    label: "LinkedIn",
    value: "linkedin.com/in/sergio-psuarez",
    href: "https://www.linkedin.com/in/sergio-psuarez",
  },
];

export default function ContactSection() {
  return (
    <Box
      component="section"
      id="contacto"
      aria-labelledby="contact-title"
      sx={(theme) => ({
        color: "white.main",
        bgcolor: "dark.focus",
        backgroundImage: `radial-gradient(circle at 12% 20%, ${alpha(
          theme.palette.primary.main,
          0.3
        )}, transparent 34%), radial-gradient(circle at 88% 82%, ${alpha(
          theme.palette.accent.main,
          0.14
        )}, transparent 28%)`,
        scrollMarginTop: 24,
      })}
    >
      <Box sx={{ ...contentWidth, py: { xs: 8, md: 12 } }}>
        <Typography
          sx={{
            ...eyebrow,
            color: "grey.300",
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            "&::before": { content: '""', width: 24, height: 2, bgcolor: "accent.main" },
          }}
        >
          CONTACTO
        </Typography>
        <Typography
          id="contact-title"
          component="h2"
          sx={{ ...sectionHeading, color: "inherit", fontWeight: 700 }}
        >
          ¿Hablamos?
        </Typography>
        <Typography sx={{ color: "grey.300", mt: 2, maxWidth: 560, lineHeight: 1.7 }}>
          Si quieres saber más sobre Gymplanner, compartir una idea o ponerte en contacto conmigo,
          puedes encontrarme aquí.
        </Typography>

        <Box
          component="ul"
          sx={{
            listStyle: "none",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: 2,
            mt: { xs: 4, md: 5 },
            p: 0,
            mb: 0,
          }}
        >
          {contactLinks.map(({ icon: Icon, label, value, href }) => {
            const isExternal = !href.startsWith("mailto:");

            return (
              <Box component="li" key={label}>
                <Link
                  href={href}
                  target={isExternal ? "_blank" : undefined}
                  rel={isExternal ? "noreferrer" : undefined}
                  aria-label={`${label}: ${value}${
                    isExternal ? " (se abre en una pestaña nueva)" : ""
                  }`}
                  sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    minHeight: 112,
                    p: 2.5,
                    color: "white.main",
                    bgcolor: alpha(theme.palette.white.main, 0.055),
                    border: "1px solid",
                    borderColor: alpha(theme.palette.white.main, 0.13),
                    borderRadius: 3,
                    transition:
                      "border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
                    "@media (hover: hover)": {
                      "&:hover": {
                        color: "white.main",
                        bgcolor: alpha(theme.palette.white.main, 0.085),
                        borderColor: alpha(theme.palette.accent.main, 0.65),
                        boxShadow: `0 16px 32px -20px ${alpha(theme.palette.black.main, 0.8)}`,
                        transform: "translateY(-2px)",
                      },
                    },
                  })}
                >
                  <Box
                    aria-hidden="true"
                    sx={(theme) => ({
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                      width: 44,
                      height: 44,
                      color: "accent.main",
                      bgcolor: alpha(theme.palette.white.main, 0.08),
                      borderRadius: 2,
                    })}
                  >
                    <Icon size={22} strokeWidth={1.7} />
                  </Box>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{label}</Typography>
                    <Typography
                      sx={{
                        color: "grey.300",
                        fontSize: "0.8125rem",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                  <ArrowUpRight size={18} aria-hidden="true" />
                </Link>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
